import { useEffect, useRef, useState } from 'react';

/**
 * LiveBalance — animated balance display.
 *
 * Flashes green when the value increases (win / deposit / redeem-in) and
 * amber when it decreases (load / spend). A tiny pulsing dot appears next
 * to the number when a fresh update has come from the provider within the
 * last `freshMs` (default 5 s) — so players can tell "live" from "cached"
 * at a glance.
 *
 * Props:
 *   value           number           required
 *   prefix          string           optional (e.g. "$")
 *   suffix          string           optional (e.g. " FP")
 *   decimals        number           default: 2
 *   freshMs         number           default: 5000
 *   syncedAt        ISO string       optional — timestamp of last server sync
 *   showLiveDot     boolean          default: true
 *   className       string           passed through to the wrapping span
 */
export default function LiveBalance({
  value,
  prefix = '',
  suffix = '',
  decimals = 2,
  freshMs = 5000,
  syncedAt = null,
  showLiveDot = true,
  className = '',
  'data-testid': dataTestId,
}) {
  const prevRef = useRef(value);
  const [pulse, setPulse] = useState(null); // 'up' | 'down' | null

  useEffect(() => {
    const prev = prevRef.current;
    if (typeof prev === 'number' && typeof value === 'number' && prev !== value) {
      setPulse(value > prev ? 'up' : 'down');
      const t = setTimeout(() => setPulse(null), 1100);
      prevRef.current = value;
      return () => clearTimeout(t);
    }
    prevRef.current = value;
  }, [value]);

  const isFresh = (() => {
    if (!syncedAt) return false;
    const syncedMs = new Date(syncedAt).getTime();
    if (Number.isNaN(syncedMs)) return false;
    return Date.now() - syncedMs <= freshMs;
  })();

  const formatted = Number(value ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  const baseColor = className.includes('text-') ? '' : 'text-[#F5D76E]';

  const pulseBg =
    pulse === 'up'
      ? 'bg-green-400/20 ring-1 ring-green-400/40'
      : pulse === 'down'
        ? 'bg-amber-400/20 ring-1 ring-amber-400/40'
        : '';
  const pulseColor =
    pulse === 'up' ? 'text-green-300' : pulse === 'down' ? 'text-amber-300' : '';

  return (
    <span
      className={`inline-flex items-center gap-1.5 font-bold transition-all duration-300 ${baseColor} ${pulseColor} ${className}`}
      data-testid={dataTestId}
    >
      <span
        className={`px-1 rounded-md transition-colors duration-500 ${pulseBg}`}
        style={{ display: 'inline-block' }}
      >
        {prefix}
        {formatted}
        {suffix}
      </span>
      {showLiveDot && isFresh && (
        <span
          className="w-2 h-2 rounded-full bg-green-400 relative"
          title="Live (synced from provider)"
          aria-label="live"
          data-testid="live-dot"
        >
          <span
            className="absolute inset-0 rounded-full bg-green-400 animate-ping"
            style={{ animationDuration: '1.2s' }}
          />
        </span>
      )}
    </span>
  );
}
